import type { KAPLAYCtx } from '../types.ts'
import type { RoomOptions } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'

export function useWellbeingRoom(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('wellbeingRoom', `sprites/rooms/wellbeing-room.png?${cacheStr}`)
  k.loadSprite('wellbeingRoomHover', `sprites/rooms/wellbeing-room--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON(
    'wellbeingRoomColliders',
    `colliders/wellbeing-room.parsed.json?${cacheStr}`,
  )

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function setWellbeingRoom(roomOpts?: RoomOptions) {
    roomOpts = roomOpts || {}

    const roomObj = k.add()

    k.onLoad(() => {
      const { data } = collidersJson

      roomObj.add([
        k.sprite('wellbeingRoom', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('background'),
        'wellbeing_room_background',
        'background',
      ])

      roomObj.add([
        k.sprite('wellbeingRoomHover', {
          width: getTiledSize(data.width),
          height: getTiledSize(data.height),
        }),
        k.layer('hover'),
        'wellbeing_room_hover',
        'hover',
      ])

      data.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        roomObj.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      roomObj.add([
        k.level(
          [
            '       ',
            '       ',
            '       ',
            '       ',
            '       ',
            '  ?    ',
            '       ',
            '       ',
            '       ',
            '       ',
            '       ',
            '  1    ',
            '       ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {
              '1': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'wellbeing_room_door_exit',
                'door_exit',
                'door',
              ],
              '?': () => [
                k.sprite('overlay', { anim: 'question' }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'wellbeing_room_info',
                'sfx_emit_action',
                'info',
              ],
            },
          },
        ),
        k.layer('objects'),
        'wellbeing_room',
      ])

      // Set player position
      k.get('player')[0]?.setPos(k.vec2(getTiledSize(2.5), getTiledSize(10)))

      const doorExitCollision = k.onCollide('player', 'wellbeing_room_door_exit', () => {
        k.flash(k.BLACK, 0.5)
        roomObj.destroy()
        doorExitCollision.cancel()
        infoCollision.cancel()
        infoCollisionEnd.cancel()
        if (roomOpts.onExit) roomOpts.onExit()
      })

      const infoCollision = k.onCollide('player', 'wellbeing_room_info', () => {
        showTextFrame(getLangString('wellbeing_room_info'))
      })

      const infoCollisionEnd = k.onCollideEnd('player', 'wellbeing_room_info', () => {
        showTextFrame()
      })
    })

    return roomObj
  }

  return {
    setWellbeingRoom,
  }
}
